import { createContext, useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { httpClient } from '../../../api/http/http-client';
import { digitalAccountApiUrl } from '../../../constants/enviroments';
import { useCustomNavigate } from '../../../utils/customHooks/useCustomNavigate';
import { BusinessRequestResponse, CurrencyResponse, PrintFormsResponse } from '../model/types';
export interface DigitalContextValue {
  state: InitialState;
  dispatch: React.Dispatch<Actions>;
}
interface Props {
  children: React.ReactNode;
}

type Actions =
  | {
      type: 'currency';
      payload: CurrencyResponse;
    }
  | {
      type: 'business-request';
      payload: BusinessRequestResponse;
    }
  | {
      type: 'print-forms';
      payload: PrintFormsResponse;
    }
  | {
      type: 'step';
      payload: 'currency' | 'business-request' | 'print-forms';
    };

interface InitialState {
  task_reference: string | null;
  active: 'currency' | 'business-request' | 'print-forms';
  currency: CurrencyResponse | null;
  business_request: BusinessRequestResponse | null;
  print_forms: PrintFormsResponse | null;
  non_working_hours: boolean;
}

const OnboardingContext = createContext<DigitalContextValue | undefined>(undefined);

const initialState: InitialState = {
  task_reference: '',
  active: 'currency',
  currency: null,
  business_request: null,
  print_forms: null,
  non_working_hours: false,
};

const OnboardingLayoutProvider = (props: Props) => {
  const navigate = useCustomNavigate();

  const [state, setState] = useState(initialState);
  const reducer = (action: Actions) => {
    switch (action.type) {
      case 'step':
        setState({
          ...state,
          active: action.payload,
          task_reference: action.payload === 'currency' ? null : state.task_reference,
        });
        break;
      case 'business-request':
        setState({
          ...state,
          active: 'business-request',
          business_request: action.payload,
        });
        break;
      case 'print-forms':
        setState({
          ...state,
          active: 'print-forms',
          print_forms: action.payload,
        });
        break;
    }

    return state;
  };

  useQuery<{ task_reference: string }>({
    queryKey: ['task_reference', state.active],
    enabled: state.active === 'currency',
    queryFn: async () => await httpClient.post(`${digitalAccountApiUrl}/create_account`),
    onSuccess: data => {
      setState(prev => ({ ...prev, task_reference: data.task_reference }));
    },
    onError: (error: any) => {
      if (error.response?.data?.error_id === 'non_working_time') {
        setState(prev => ({ ...prev, non_working_hours: true }));

        return;
      }
      if (error.response?.data?.result === 'go_to_register') {
        navigate('register');

        return;
      }
    },
  });

  return (
    <OnboardingContext.Provider value={{ state, dispatch: reducer }}>
      {props.children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within a OnboardingLayoutProvider');
  }

  return context;
};

export default OnboardingLayoutProvider;
